import { ChangeDetectionStrategy, Component, computed, Inject, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import { RoleTextPipe } from "../../pipes/role-text.pipe";
import { TooltipModule } from "primeng/tooltip";
import { TCompanyRole } from "../../models/company-role";
import { ISelectedPlan, takeSelectedPlan } from "../../utils/selected-plan";
import { toSignal } from "@angular/core/rxjs-interop";
import { roleAtLeast$ } from "../../global-states/session/role-at-least";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import {
	CardGroupComponent,
	EPageLayout,
	ESeverity,
	IToastService,
	PageLayoutComponent,
	TabNavigationStateService,
	tabNavigationStateServiceInjectionToken,
	IconComponent,
	formatPrice,
	toastServiceInjectionToken
} from '@zaeper/angular-dashboard-view-lib';
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { ButtonModule } from "primeng/button";
import { TableModule } from "primeng/table";
import { MenuItem } from "primeng/api";
import { SliderModule } from "primeng/slider";
import { CardModule } from "primeng/card";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { SelectButtonModule } from "primeng/selectbutton";
import { sessionStateServiceInjectionToken } from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import { Observable, Subscription } from "rxjs";
import { ILicense } from "../../models/i-license";
import { IBillingRestClient } from "../../rest-clients/billing/i-billing-rest.client";
import { billingRestClientInjectionToken } from "../../rest-clients/billing/billing-rest-client-injection-token";
import { IAddPaymentMethodModalService } from "../../modals/add-payment-method-modal/i-add-payment-method-modal.service";
import {
	addPaymentMethodModalServiceInjectionToken
} from "../../modals/add-payment-method-modal/add-payment-method-modal-service-injection-token";
import { IStripeService } from "../../services/stripe/i-stripe.service";
import { stripeServiceInjectionToken } from "../../services/stripe/stripe-service-injection-token";
import { ISubscription } from "../../models/i-subscription";
import { ISubscriptionItemForm } from "../../rest-clients/billing/forms/i-subscription-item-form";
import { EBillingInterval, IPriceList, unitPriceOf, YEARLY_MONTHS_FREE } from "../../models/i-pricing-config";

const PRIVATE_REPOSITORY_FEATURE = "private-repository";

/** Licence feature id → its signage product key; the private repository is the registry's own subscription. */
const FEATURE_PRODUCT_KEYS: Record<string, string> = {
	"group-sync": "signage.group-sync",
	"live-view": "signage.live-view"
};

interface FeatureDisplay {
	featureId: string;
	name: string;
	description: string;
	enabled: boolean;
	locked: boolean;
}

interface PricingCard {
	label: string;
	icon: string;
	currentUnitPrice: number;
	currentInterval: EBillingInterval;
	newUnitPrice: number;
	newInterval: EBillingInterval;
	currentQuantity: number;
	newQuantity: number;
	type: 'license' | 'feature';
}

@Component({
	selector: 'app-extras-overview-page',
	templateUrl: './features-overview-page.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		RouterModule,
		ButtonModule,
		TableModule,
		SliderModule,
		CardModule,
		ToggleSwitchModule,
		SelectButtonModule,
		ZDictionaryPipe,
		RoleTextPipe,
		TooltipModule,
		ZTemplateDirective,
		PageLayoutComponent,
		IconComponent,
		CardGroupComponent
	],
	providers: [
		{
			provide: tabNavigationStateServiceInjectionToken,
			useClass: TabNavigationStateService
		}
	]
})
export class FeaturesOverviewPageComponent implements OnDestroy {
	public readonly ePageLayout: typeof EPageLayout = EPageLayout;

	public readonly navigation: MenuItem[] = [
		{
			icon: 'pi pi-home',
			routerLink: '/de/',
		}, {
			label: 'Features',
			routerLink: '/de/features',
		},
	];

	public readonly activeLicense$: Observable<ILicense | null>;
	/** The website calculator's plan, kept so a later licence update doesn't undo the preselection. */
	private _preselectedPlan: ISelectedPlan | null = null;
	private readonly _subscriptions: Subscription = new Subscription();

	public readonly features = signal<FeatureDisplay[]>([]);
	public readonly maxClouds = signal(1);
	public readonly maxGroups = signal(1);
	public readonly maxReceivers = signal(1);
	public readonly maxUsers = signal(1);
	public readonly isSaving = signal(false);
	/** Buying, upgrading and switching the interval need Manager; cancelling everything (back to free) Admin. */
	public readonly isManager: Signal<boolean | undefined>;
	public readonly isAdmin: Signal<boolean | undefined>;
	/** The role saving needs that the user lacks: Manager to buy, Admin to go back to the free package. */
	public readonly requiredRole = computed<TCompanyRole | null>(() => {
		if (!this.isManager()) return "MANAGER";
		return this.isNewStarterPackage() && !this.isCurrentStarterPackage() && !this.isAdmin() ? "ADMIN" : null;
	});

	/** The interval the signage subscription is billed in (all items alike), and the one chosen on the page. */
	public readonly currentInterval = signal<EBillingInterval>('MONTHLY');
	public readonly billingInterval = signal<EBillingInterval>('MONTHLY');
	public readonly intervalOptions: { label: string, value: EBillingInterval }[] = [
		{ label: 'Monatlich', value: 'MONTHLY' },
		{ label: `Jährlich (${YEARLY_MONTHS_FREE} Monate gratis)`, value: 'YEARLY' }
	];

	/** The company's prices in its currency (Stripe, through the billing endpoint); null until loaded. */
	public readonly prices = signal<IPriceList | null>(null);

	public readonly isCurrentStarterPackage = computed(() => {
		const l = this._currentLicense;
		const allLicensesDefault = (l?.maxClouds ?? 1) === 1 && (l?.maxGroups ?? 1) === 1
			&& (l?.maxReceivers ?? 1) === 1 && (l?.maxUsers ?? 1) === 1;
		const noFeaturesEnabled = !this._currentLicense?.enabledFeatureIds?.length;
		return allLicensesDefault && noFeaturesEnabled;
	});

	public readonly isNewStarterPackage = computed(() => {
		const allLicensesDefault = this.maxClouds() === 1 && this.maxGroups() === 1
			&& this.maxReceivers() === 1 && this.maxUsers() === 1;
		const noFeaturesEnabled = !this.features().some(f => f.enabled && !f.locked);
		return allLicensesDefault && noFeaturesEnabled;
	});

	public readonly pricingCards = computed<PricingCard[]>(() => {
		const license = this._currentLicense;
		const feats = this.features();
		const currentFree = this.isCurrentStarterPackage();
		const newFree = this.isNewStarterPackage();
		const card = (label: string, icon: string, productKey: string, currentQuantity: number, newQuantity: number, type: 'license' | 'feature'): PricingCard => {
			const prices = this.prices();
			const current = unitPriceOf(prices, productKey, this.currentInterval());
			const next = unitPriceOf(prices, productKey, this.billingInterval());
			return {
				label,
				icon,
				currentUnitPrice: current?.price ?? 0,
				currentInterval: current?.interval ?? this.currentInterval(),
				newUnitPrice: next?.price ?? 0,
				newInterval: next?.interval ?? this.billingInterval(),
				currentQuantity,
				newQuantity,
				type
			};
		};

		return [
			card('Cloud-Lizenz', 'pi pi-cloud', "signage.cloud-license",
				currentFree ? 0 : (license?.maxClouds ?? 1), newFree ? 0 : this.maxClouds(), 'license'),
			card('Gruppen-Lizenz', 'pi pi-sitemap', "signage.group-license",
				currentFree ? 0 : (license?.maxGroups ?? 1), newFree ? 0 : this.maxGroups(), 'license'),
			card('Receiver-Lizenz', 'pi pi-desktop', "signage.receiver-license",
				currentFree ? 0 : (license?.maxReceivers ?? 1), newFree ? 0 : this.maxReceivers(), 'license'),
			card('User-Lizenz', 'pi pi-user', "signage.user-license",
				currentFree ? 0 : (license?.maxUsers ?? 1), newFree ? 0 : this.maxUsers(), 'license'),
			card('DeviceSync-Lizenz', 'pi pi-sync', "signage.group-sync",
				this._isFeatureCurrentlyEnabled('group-sync') ? 1 : 0, this._isFeatureNewlyEnabled(feats, 'group-sync') ? 1 : 0, 'feature'),
			card('Live-View-Lizenz', 'pi pi-eye', "signage.live-view",
				this._isFeatureCurrentlyEnabled('live-view') ? 1 : 0, this._isFeatureNewlyEnabled(feats, 'live-view') ? 1 : 0, 'feature'),
			card('Private-Repository-Lizenz', 'pi pi-lock', "registry.private-repository",
				this._isFeatureCurrentlyEnabled(PRIVATE_REPOSITORY_FEATURE) ? 1 : 0, this._isFeatureNewlyEnabled(feats, PRIVATE_REPOSITORY_FEATURE) ? 1 : 0, 'feature')
		];
	});

	public readonly totalCurrentPrice = computed(() =>
		this.pricingCards().filter(c => c.currentInterval === 'MONTHLY').reduce((sum, c) => sum + c.currentUnitPrice * c.currentQuantity, 0)
	);

	public readonly totalNewPrice = computed(() =>
		this.pricingCards().filter(c => c.newInterval === 'MONTHLY').reduce((sum, c) => sum + c.newUnitPrice * c.newQuantity, 0)
	);

	// Yearly items (yearly signage billing and the 12-month private repository) are totalled apart from the monthly price.
	public readonly totalCurrentYearlyPrice = computed(() =>
		this.pricingCards().filter(c => c.currentInterval === 'YEARLY').reduce((sum, c) => sum + c.currentUnitPrice * c.currentQuantity, 0)
	);

	public readonly totalNewYearlyPrice = computed(() =>
		this.pricingCards().filter(c => c.newInterval === 'YEARLY').reduce((sum, c) => sum + c.newUnitPrice * c.newQuantity, 0)
	);

	private _currentLicense: ILicense | null = null;
	private _activeSubscription: ISubscription | null = null;

	public readonly hasChanges = computed(() => {
		if (!this._currentLicense) return false;

		const licenseChanged = this.maxClouds() !== this._currentLicense.maxClouds
			|| this.maxGroups() !== this._currentLicense.maxGroups
			|| this.maxReceivers() !== this._currentLicense.maxReceivers
			|| this.maxUsers() !== this._currentLicense.maxUsers;

		const currentEnabledIds = this._currentLicense.enabledFeatureIds;
		const featuresChanged = this.features().some(f =>
			!f.locked && f.enabled !== currentEnabledIds.includes(f.featureId)
		);

		// Switching the interval only means something for a paid package.
		const intervalChanged = this.billingInterval() !== this.currentInterval() && !this.isNewStarterPackage();

		return licenseChanged || featuresChanged || intervalChanged;
	});

	private static readonly _FEATURE_DEFINITIONS: { featureId: string, name: string, description: string, locked?: boolean }[] = [
		{
			featureId: "cloud-management",
			name: "Cloud Verwaltung",
			description: "Verwalten Sie Ihre Clouds, Gruppen und Receiver zentral über das Dashboard.",
			locked: true
		},
		{
			featureId: "group-sync",
			name: "Gruppen Synchronisierung",
			description: "Mit der Gruppen-Synchronisation können Sie die Einstellungen aller Receiver innerhalb einer Gruppe synchronisieren. Halten Sie mehrere Receiver stets auf dem gleichen Stand, indem Sie Änderungen zentral vornehmen und automatisch auf alle Geräte in der Gruppe übertragen."
		},
		{
			featureId: "live-view",
			name: "Live-Ansicht",
			description: "Mit der Live-Übertragung können Sie in Echtzeit überprüfen, ob die aktuellen Daten erfolgreich auf den Receiver übertragen wurden, ohne vor Ort sein zu müssen."
		},
		{
			featureId: "private-repository",
			name: "Private Repository",
			description: "Mit dem privaten Repository können Sie eigene Anwendungen hochladen und exklusiv für Ihr Unternehmen bereitstellen."
		}
	];

	constructor(
		@Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
		@Inject(billingRestClientInjectionToken) private readonly _billingRestClient: IBillingRestClient,
		@Inject(addPaymentMethodModalServiceInjectionToken) private readonly _addPaymentMethodModalService: IAddPaymentMethodModalService,
		@Inject(stripeServiceInjectionToken) private readonly _stripeService: IStripeService,
		@Inject(toastServiceInjectionToken) private readonly _toastService: IToastService
	) {

		this.activeLicense$ = this._sessionStateService.activeLicense$;
		this.isManager = toSignal(roleAtLeast$(this._sessionStateService, "MANAGER"));
		this.isAdmin = toSignal(roleAtLeast$(this._sessionStateService, "ADMIN"));

		this._subscriptions.add(this.activeLicense$.subscribe((license: ILicense | null) => {
			this._currentLicense = license;
			this._initializeFeatures(license);
			this._initializeLicenseValues(license);
			if (this._preselectedPlan) {
				this._applySelectedPlan(this._preselectedPlan);
			}
		}));

		this._loadActiveSubscription();
		this._loadPrices();
	}

	/** A price in the company's currency, formatted for the locale (CHF 1’380.00, $1,380.00). */
	public format(amount: number): string {
		const currency = this.prices()?.currency;
		return currency ? formatPrice(Math.round(amount * 100), currency) : "–";
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
	}

	public async save(): Promise<void> {
		const company = this._sessionStateService.getActiveCompany();
		if (!company || !this._currentLicense) return;

		this.isSaving.set(true);

		const enabledFeatureIds: string[] = this.features()
			.filter(f => f.enabled && !f.locked)
			.map(f => f.featureId);

		const isDowngradingToStarter = this.isNewStarterPackage();
		const isCurrentlyStarter = this.isCurrentStarterPackage();
		const hasActiveSubscription = this._activeSubscription !== null
			&& (this._activeSubscription.status === "ACTIVE" || this._activeSubscription.status === "TRIALING");

		try {
			// Case 1: Downgrading to Starter → cancel subscription if active
			if (isDowngradingToStarter && hasActiveSubscription) {
				await this._cancelActiveSubscription(company.id);
			}
			// Case 2: Upgrading from Starter → ensure payment method exists, then create subscription
			else if (!isDowngradingToStarter && isCurrentlyStarter && !hasActiveSubscription) {
				const hasPaymentMethod = await this._ensurePaymentMethodExists(company.id);
				if (!hasPaymentMethod) {
					this.isSaving.set(false);
					return;
				}
				await this._createNewSubscription(company.id, enabledFeatureIds);
			}
			// Case 3: Modifying existing subscription
			else if (!isDowngradingToStarter && hasActiveSubscription) {
				await this._updateExistingSubscription(company.id, enabledFeatureIds);
			}

			// The private repository is bought (or cancelled) through the registry
			const hadPrivateRepository = this._isFeatureCurrentlyEnabled(PRIVATE_REPOSITORY_FEATURE);
			const wantsPrivateRepository = enabledFeatureIds.includes(PRIVATE_REPOSITORY_FEATURE);
			if (wantsPrivateRepository && !hadPrivateRepository) {
				const purchased = await this._purchasePrivateRepository(company.id);
				if (!purchased) {
					enabledFeatureIds.splice(enabledFeatureIds.indexOf(PRIVATE_REPOSITORY_FEATURE), 1);
				}
			} else if (!wantsPrivateRepository && hadPrivateRepository) {
				await this._billingRestClient.cancelRegistryLicense(company.id);
			}

			// license-svc derives the licence from the subscription (billing event); until it arrives the page
			// shows what was bought.
			this._currentLicense = {
				...this._currentLicense,
				maxClouds: this.maxClouds(),
				maxGroups: this.maxGroups(),
				maxReceivers: this.maxReceivers(),
				maxUsers: this.maxUsers(),
				enabledFeatureIds
			};
			this._sessionStateService.setActiveLicense(this._currentLicense);

			this._toastService.setMessage({
				title: "Erfolgreich",
				detail: "Einstellungen wurden gespeichert.",
				severity: ESeverity.success
			});

			// Refresh subscription state
			await this._loadActiveSubscription();
		} catch (error: unknown) {
			const httpError = error as { error?: { message?: string } };
			const message = httpError?.error?.message || "Einstellungen konnten nicht gespeichert werden.";
			this._toastService.setMessage({
				title: "Fehler",
				detail: message,
				severity: ESeverity.error
			});
		} finally {
			this.isSaving.set(false);
		}
	}

	public clampAndSet(sig: WritableSignal<number>, value: number, min: number, max: number): void {
		sig.set(Math.max(min, Math.min(max, value)));
	}

	public onFeatureToggle(featureId: string, enabled: boolean): void {
		this.features.update(features =>
			features.map(f => f.featureId === featureId ? { ...f, enabled } : f)
		);
	}

	private async _loadActiveSubscription(): Promise<void> {
		const company = this._sessionStateService.getActiveCompany();
		if (!company) return;

		try {
			const subscriptions = await this._billingRestClient.getSubscriptions(company.id);
			this._activeSubscription = subscriptions.find(
				s => (s.status === "ACTIVE" || s.status === "TRIALING")
					&& s.items.some(i => i.productKey.startsWith("signage."))
			) ?? null;
		} catch {
			this._activeSubscription = null;
		}
		const interval: EBillingInterval = this._activeSubscription?.billingInterval === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
		this.currentInterval.set(interval);
		this.billingInterval.set(interval);
		// A plan from the website's calculator only preselects a first package, never a running subscription.
		const plan: ISelectedPlan | null = takeSelectedPlan();
		if (plan && !this._activeSubscription) {
			this._preselectedPlan = plan;
			this._applySelectedPlan(plan);
		}
	}

	private _applySelectedPlan(plan: ISelectedPlan): void {
		this.maxClouds.set(plan.maxClouds);
		this.maxGroups.set(plan.maxGroups);
		this.maxReceivers.set(plan.maxReceivers);
		this.maxUsers.set(plan.maxUsers);
		this.billingInterval.set(plan.interval);
		this.features.update(features => features.map(f => f.locked ? f : { ...f, enabled: plan.featureIds.includes(f.featureId) }));
	}

	private async _loadPrices(): Promise<void> {
		const company = this._sessionStateService.getActiveCompany();
		if (!company) return;

		try {
			this.prices.set(await this._billingRestClient.getPrices(company.id));
		} catch {
			this.prices.set(null);
		}
	}

	private async _ensurePaymentMethodExists(companyId: string): Promise<boolean> {
		try {
			const paymentMethods = await this._billingRestClient.listPaymentMethods(companyId);
			if (paymentMethods.length > 0) {
				return true;
			}
		} catch {
			// If we can't check, prompt to add
		}

		// No payment method found — open the add payment method modal
		this._toastService.setMessage({
			title: "Zahlungsmethode erforderlich",
			detail: "Bitte fügen Sie eine Zahlungsmethode hinzu, um fortzufahren.",
			severity: ESeverity.info
		});

		const paymentMethodAdded = await this._addPaymentMethodModalService.open(companyId);
		return paymentMethodAdded;
	}

	/** The complete item list of the signage subscription; the backend removes items it no longer lists. */
	private _buildSubscriptionItems(enabledFeatureIds: string[]): ISubscriptionItemForm[] {
		const items: ISubscriptionItemForm[] = [
			{ productKey: "signage.cloud-license", quantity: this.maxClouds() },
			{ productKey: "signage.group-license", quantity: this.maxGroups() },
			{ productKey: "signage.receiver-license", quantity: this.maxReceivers() },
			{ productKey: "signage.user-license", quantity: this.maxUsers() }
		];

		// The private repository isn't part of this subscription: it is a private registry in the Zaeper
		// registry, bought there as its own yearly subscription.
		for (const [featureId, productKey] of Object.entries(FEATURE_PRODUCT_KEYS)) {
			if (enabledFeatureIds.includes(featureId)) {
				items.push({ productKey, quantity: 1 });
			}
		}

		return items;
	}

	private async _createNewSubscription(companyId: string, enabledFeatureIds: string[]): Promise<void> {
		const items = this._buildSubscriptionItems(enabledFeatureIds);

		const subscription = await this._billingRestClient.createSubscription(companyId, {
			interval: this.billingInterval(),
			items
		});

		// Handle 3D Secure / SCA if required
		if (subscription.status === "INCOMPLETE") {
			await this._handleScaChallenge(subscription);
		}

		this._activeSubscription = subscription;
	}

	private async _updateExistingSubscription(companyId: string, enabledFeatureIds: string[]): Promise<void> {
		if (!this._activeSubscription) return;

		const items = this._buildSubscriptionItems(enabledFeatureIds);

		const subscription = await this._billingRestClient.updateSubscriptionItems(companyId, {
			stripeSubscriptionId: this._activeSubscription.stripeSubscriptionId,
			interval: this.billingInterval(),
			items
		});

		this._activeSubscription = subscription;
	}

	private async _cancelActiveSubscription(companyId: string): Promise<void> {
		if (!this._activeSubscription) return;

		await this._billingRestClient.cancelSubscription(companyId, {
			stripeSubscriptionId: this._activeSubscription.stripeSubscriptionId,
			cancelAtPeriodEnd: true
		});

		this._activeSubscription = null;
	}

	/**
	 * Buys a private registry in the Zaeper registry for the company (its organization account).
	 * The registry needs the company's namespace first; the feature turns on once the registry
	 * reports the license as paid.
	 */
	private async _purchasePrivateRepository(companyId: string): Promise<boolean> {
		const hasPaymentMethod = await this._ensurePaymentMethodExists(companyId);
		if (!hasPaymentMethod) {
			return false;
		}
		try {
			const purchase = await this._billingRestClient.purchaseRegistryLicense(companyId);
			if (purchase.status === "INCOMPLETE") {
				await this._handleScaChallenge({ stripeSubscriptionId: purchase.stripeSubscriptionId, clientSecret: purchase.clientSecret } as ISubscription);
			}
			return true;
		} catch (error: unknown) {
			const code = (error as { error?: { error?: string } })?.error?.error;
			if (code === "license_exists") {
				return true;
			}
			this._toastService.setMessage({
				title: "Private Repository",
				detail: code === "namespace_missing"
					? "Bitte legen Sie zuerst im Developer-Dashboard einen Namespace für Ihre Organisation fest."
					: "Das Private Repository konnte nicht gebucht werden.",
				severity: ESeverity.error
			});
			return false;
		}
	}

	private async _handleScaChallenge(subscription: ISubscription): Promise<void> {
		const stripe = await this._stripeService.getStripe();
		if (!stripe) {
			throw new Error("Stripe konnte nicht geladen werden");
		}

		if (!subscription.clientSecret) {
			this._toastService.setMessage({
				title: "Authentifizierung erforderlich",
				detail: "Bitte überprüfen Sie Ihre E-Mails für weitere Anweisungen zur Zahlungsbestätigung.",
				severity: ESeverity.info
			});
			return;
		}

		const { error } = await stripe.confirmPayment({
			clientSecret: subscription.clientSecret,
			confirmParams: {
				return_url: window.location.href
			}
		});

		if (error) {
			throw new Error(error.message ?? "Zahlungsbestätigung fehlgeschlagen");
		}
	}

	private _initializeFeatures(license: ILicense | null): void {
		const enabledFeatureIds: string[] = license?.enabledFeatureIds ?? [];

		this.features.set(FeaturesOverviewPageComponent._FEATURE_DEFINITIONS.map(def => ({
			featureId: def.featureId,
			name: def.name,
			description: def.description,
			enabled: def.locked || enabledFeatureIds.includes(def.featureId),
			locked: def.locked ?? false
		})));
	}

	private _initializeLicenseValues(license: ILicense | null): void {
		if (!license) return;

		this.maxClouds.set(license.maxClouds);
		this.maxGroups.set(license.maxGroups);
		this.maxReceivers.set(license.maxReceivers);
		this.maxUsers.set(license.maxUsers);
	}

	private _isFeatureCurrentlyEnabled(featureId: string): boolean {
		return this._currentLicense?.enabledFeatureIds.includes(featureId) ?? false;
	}

	private _isFeatureNewlyEnabled(feats: FeatureDisplay[], featureId: string): boolean {
		return feats.find(f => f.featureId === featureId)?.enabled ?? false;
	}
}
